import { CircleProfilePhoto } from "@cmp-src/map/InteractiveZones/CircleProfilePhoto";
import { getActualSchedule } from "@cmp-src/map/InteractiveZones/helpers";
import type { AuthenticatedUser, IResource } from "@gettactic/api";
import { getVisibleAssignment } from "@gettactic/helpers";
import { classNames } from "@gettactic/helpers/src/css/classNames";
import { mergeRefs } from "@gettactic/helpers/src/dom/mergeRef";
import {
  InteractiveZone,
  InteractiveZonesInfo
} from "@gettactic/helpers/src/map/types";
import { useSettings } from "@gettactic/hooks";
import { IResourceWithSchedules } from "@gettactic/hooks";
import { useMap } from "@webV2/map/MapProvider";
import React, { useLayoutEffect, useRef, useState } from "react";

type MapViewSvgInteractiveZonesProps = {
  zonesInfo: InteractiveZonesInfo;
  resources: IResourceWithSchedules[];
  totalResources?: IResource[];
  selectedResource: IResource | null;
  resourceSelected?: (id: string) => void;
  openReservation?: (res: IResource) => void;
  highlightResource: (resId: string | null, target: Element | null) => void;
  lastAccessedDeskResourceId: string | null;
  lastAccessedRoomResourceId: string | null;
  authenticatedUser: AuthenticatedUser | undefined;
  startDate?: Date | null;
  colleagues?: string[];
  isMobile?: boolean;
  calendarDates?: Date[];
  focusedCircleRef?: React.MutableRefObject<SVGSVGElement | null>;
  highlightCircleRef?: React.MutableRefObject<SVGSVGElement | null>;
  startHidden?: boolean;
};

// NOTE: This component is used by screens that do not have an authenticatedUser.
//       Do not add the useAuthenticated hook here.
export const MapViewSvgInteractiveZones = React.forwardRef<
  SVGSVGElement,
  MapViewSvgInteractiveZonesProps
>(
  (
    {
      highlightResource,
      selectedResource,
      openReservation,
      zonesInfo,
      resources,
      totalResources,
      resourceSelected,
      lastAccessedDeskResourceId,
      lastAccessedRoomResourceId,
      authenticatedUser,
      startDate,
      colleagues,
      calendarDates,
      isMobile,
      highlightCircleRef,
      focusedCircleRef,
      startHidden
    },
    ref
  ) => {
    const { mapInfo } = zonesInfo;
    const svg = useRef<SVGSVGElement>(null);
    const [chrome96Bug, setChrome96Bug] = useState(false);
    const { isOrgPrivacyEnabled: isPrivacyEnabled } = useSettings();

    const [hoveringCircle, setHoveringCircle] =
      React.useState<SVGElement | null>(null);

    const [focusCircle, setFocusCircle] = React.useState<SVGElement | null>(
      null
    );

    const { events, resourceInReservation, isOnReservationModal } = useMap();

    const moveCircleToResource = (
      circleElement: SVGElement,
      resource: IResource | null
    ) => {
      if (!circleElement) return;

      if (!resource || !svg.current) {
        circleElement.style.display = "none";
        return;
      }

      const circle = Array.from(
        svg?.current.querySelectorAll(`#interactive-${resource.id} circle`) ??
          []
      ).find((circle) => {
        const style = window.getComputedStyle(circle);
        return style.display !== "none";
      }) as SVGSVGElement | undefined;

      if (!circle) {
        circleElement.style.display = "none";
        return;
      }

      const cx = Number.parseFloat(String(circle.getAttribute("cx") || 0));
      const cy = Number.parseFloat(String(circle.getAttribute("cy") || 0));

      const point = svg.current.createSVGPoint();
      point.x = cx;
      point.y = cy;

      const ctm = circle.getCTM();
      const transformed = point.matrixTransform(ctm || undefined);

      circleElement.setAttribute("style", "display:block");
      circleElement.setAttribute(
        "transform",
        `translate(${(transformed.x || 0) - 37}, ${(transformed.y || 0) - 37})`
      );

      circleElement.style.display = "block";
    };

    React.useEffect(() => {
      const onHighlightUserResource = (resources: IResource[] | null) => {
        if (!hoveringCircle || !svg.current) return;

        svg.current
          .querySelectorAll(".user-highlight-circle")
          .forEach((circle) => {
            circle.remove();
          });

        resources?.forEach((resource) => {
          const circle = hoveringCircle.cloneNode(true) as SVGElement;
          circle.classList.add("user-highlight-circle");
          circle.setAttribute("style", "display:block");
          svg.current?.prepend(circle);
          moveCircleToResource(circle, resource);
          return circle;
        });
      };

      events.register("onHighlightUserResource", onHighlightUserResource);

      return () => {
        events.unregister("onHighlightUserResource", onHighlightUserResource);
      };
    }, [hoveringCircle, zonesInfo.zones]);

    React.useEffect(() => {
      const onHighlightResource = (resource: IResource | null) => {
        if (!hoveringCircle) return;

        moveCircleToResource(hoveringCircle, resource);
      };
      events.register("onHighlightResource", onHighlightResource);

      return () => {
        events.unregister("onHighlightResource", onHighlightResource);
      };
    }, [hoveringCircle, zonesInfo.zones]);

    React.useEffect(() => {
      const onFocusResource = (resource: IResource | null) => {
        if (!focusCircle) return;

        moveCircleToResource(focusCircle, resource);
      };
      events.register("onFocusResource", onFocusResource);

      return () => {
        events.unregister("onFocusResource", onFocusResource);
      };
    }, [focusCircle, zonesInfo.zones]);

    useLayoutEffect(() => {
      if (!svg.current) {
        return;
      }
      // This code fills partially reserved resources
      // adding class disabled to the first n reserved child.
      //
      // We need to do some low level DOM stuff because
      // child of interactive-zones aren't handled by
      // react. We can improve this if we parse the entire
      // SVG in the future.
      //
      svg.current.querySelectorAll(".interactive-zone").forEach((d) => {
        const reserved = Number.parseInt(
          d.getAttribute("data-reserved") || "0",
          10
        );
        d.querySelectorAll("path").forEach((rect, index) => {
          rect.classList.toggle("disabled", index < reserved);
        });
      });

      // After loading the SVG we add a class chrome96Bug to
      // hide svg text nodes, after some ms we remove it
      // so we trigger a new position
      setChrome96Bug(true);
      setTimeout(() => {
        if (svg.current) {
          setChrome96Bug(false);
        }
      }, 200);
    }, [svg, zonesInfo, resources]);

    if (!mapInfo) {
      return null;
    }

    // Temporary function for Instructure contract
    function openReservationIfSchedulable(resId: string) {
      if (!resources) {
        return;
      }
      const res = resources?.find((x) => x.id === resId);
      if (res && openReservation) {
        openReservation(res);
      }

      return;
    }

    return (
      <svg
        ref={mergeRefs([svg, ref])}
        xmlns="http://www.w3.org/2000/svg"
        {...mapInfo.attrs}
        className={classNames(
          "interactive-zone-svg",
          chrome96Bug && "chrome96Bug"
        )}
        style={
          startHidden
            ? { opacity: 0, transition: "opacity 0.3s ease-in-out" }
            : undefined
        }
      >
        <g
          style={{ display: "none" }}
          ref={mergeRefs([setHoveringCircle, highlightCircleRef || null])}
          className="hh-circle map-pulse-highlight absolute top-0 left-0 hidden overflow-hidden"
        >
          <circle
            cx="37.2041"
            cy="37"
            r="37"
            fill="url(#paint0_radial_1221_74399)"
          />
          <rect
            x="21.21"
            y="21"
            width="32"
            height="32"
            rx="16"
            stroke="#4F48F7"
            strokeWidth="4"
          />
          <defs>
            <radialGradient
              id="paint0_radial_1221_74399"
              cx="0"
              cy="0"
              r="1"
              gradientUnits="userSpaceOnUse"
              gradientTransform="translate(37.2041 37) rotate(90) scale(37)"
            >
              <stop offset="0.655" stopColor="#656CFF" stopOpacity="0.4" />
              <stop offset="1" stopColor="#656CFF" />
            </radialGradient>
          </defs>
        </g>
        <g
          ref={mergeRefs([setFocusCircle, focusedCircleRef || null])}
          className="map-pulse hh-circle absolute top-0 left-0 hidden"
        >
          <circle
            cx="37.2041"
            cy="37"
            r="37"
            fill="url(#paint0_radial_1221_74398)"
          />
          <rect
            x="21.21"
            y="21"
            width="32"
            height="32"
            rx="16"
            stroke="#4F48F7"
            strokeWidth="4"
          />
          <defs>
            <radialGradient
              id="paint0_radial_1221_74398"
              cx="0"
              cy="0"
              r="1"
              gradientUnits="userSpaceOnUse"
              gradientTransform="translate(37.2041 37) rotate(90) scale(37)"
            >
              <stop offset="0.655" stopColor="#656CFF" stopOpacity="0.4" />
              <stop offset="1" stopColor="#656CFF" />
            </radialGradient>
          </defs>
        </g>

        {/* biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation> */}
        <defs dangerouslySetInnerHTML={{ __html: mapInfo.defs }} />
        {zonesInfo.zones.map((zone: InteractiveZone) => {
          const res = resources.find((x) => x.id === zone.id);
          const notAssigned = !res;
          const requiresApproval =
            !!res && res.is_approvable && res.available_slots > 0;

          const isMultipleDates = (calendarDates?.length || 0) > 1;
          const disabled = !!res && res.available_slots === 0;
          const reservedSlots = res?.reserved_slots || 0;
          const hasSchedules = (res?.schedules?.length ?? 0) > 0;
          const schedules = (res?.schedules || []).map((schedule) => ({
            ...schedule,
            user: schedule.user || schedule.slots?.[0]?.user || null
          }));
          const actualSchedule = getActualSchedule(schedules, startDate);
          const authUserId = authenticatedUser?.user?.id;
          const scheduledUserId = schedules[0]?.user?.id;
          const showCircleProfilePhoto =
            !isMultipleDates &&
            disabled &&
            hasSchedules &&
            actualSchedule &&
            (res.type === "desk" || res.type === "parking_space") &&
            res.total_slots === 1;

          const assignedUser = getVisibleAssignment(
            totalResources,
            zone.id,
            true
          );

          const handleCircleIndicator = () => {
            if (scheduledUserId === authUserId) {
              return (
                <circle
                  className="animate-pulse text-primary"
                  {...zone.circle}
                  transform={
                    zone?.mapBuilderContainerTransformation ||
                    zone?.containerTransformation
                  }
                  cx={zone.circle?.cx && zone.circle.cx + 20}
                  cy={zone.circle?.cy && zone.circle.cy - 20}
                  r={10}
                  fill="currentColor"
                  stroke="none"
                />
              );
            }

            return null;
          };

          return (
            <React.Fragment key={`interactive-${zone.id}`}>
              <g
                data-reserved={reservedSlots}
                id={`interactive-${zone.id}`}
                onClick={(ev) => {
                  ev.preventDefault();
                  if (isOnReservationModal) {
                    highlightResource(zone.id, null);
                  }

                  const isCmdOrCtrl = ev.metaKey || ev.ctrlKey;
                  // Check if Cmd (Mac) or Ctrl (Windows/Linux) key is pressed
                  if (isCmdOrCtrl) {
                    openReservationIfSchedulable(zone.id);
                  }
                  if (resourceSelected && !isCmdOrCtrl) {
                    resourceSelected(zone.id);
                  } else if (openReservation && !isOnReservationModal && !isCmdOrCtrl) {
                    openReservationIfSchedulable(zone.id);
                  } else if (isOnReservationModal) {
                    if (res) {
                      openReservation?.(res);
                    }
                  }
                }}
                className={
                  isMobile && !res
                    ? "disabled"
                    : classNames(
                        !!selectedResource &&
                          selectedResource.id === zone.id &&
                          "selected",
                        isOnReservationModal &&
                          resourceInReservation === zone.id &&
                          "selected",
                        (lastAccessedDeskResourceId === zone.id ||
                          lastAccessedRoomResourceId === zone.id) &&
                          "last-used",
                        "interactive-zone",
                        disabled || (hasSchedules && isMultipleDates)
                          ? "disabled"
                          : undefined,
                        notAssigned && "notAssigned",
                        requiresApproval && "requires-approval",
                        !disabled && hasSchedules && "bookeable-with-schedules",
                        disabled &&
                          hasSchedules &&
                          !isMultipleDates &&
                          "not-bookeable-with-schedules",
                        disabled &&
                          !hasSchedules &&
                          "not-bookeable-without-schedules",
                        showCircleProfilePhoto && "show-circle-profile-photo"
                      )
                }
                onMouseEnter={({ currentTarget }) => {
                  if (!res) {
                    return;
                  }
                  highlightResource(res.id, currentTarget);
                }}
                onMouseLeave={() => {
                  highlightResource(null, null);
                }}
              >
                <g
                  className="circle-default"
                  /* biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation> */
                  dangerouslySetInnerHTML={{ __html: zone.html }}
                  transform={zone.mapBuilderContainerTransformation}
                />

                {assignedUser && zone?.circle ? (
                  <CircleProfilePhoto
                    privacy={
                      isPrivacyEnabled &&
                      assignedUser.id !== authenticatedUser?.user?.id
                    }
                    zone={zone}
                    id={res?.id ?? assignedUser.id}
                    profilePhoto={assignedUser.profile_photo_url ?? ""}
                    name={assignedUser.name}
                    hasCheckedIn={false}
                    restricted={true}
                  />
                ) : null}

                {showCircleProfilePhoto && zone?.circle ? (
                  <CircleProfilePhoto
                    privacy={
                      isPrivacyEnabled &&
                      actualSchedule.user.id !== authenticatedUser?.user?.id
                    }
                    zone={zone}
                    id={res.id}
                    profilePhoto={actualSchedule.user.profile_photo_url ?? ""}
                    name={actualSchedule.user.name}
                    hasCheckedIn={actualSchedule.has_checked_in}
                    grayscale={actualSchedule.user.roles?.includes("deleted")}
                  />
                ) : null}
                {showCircleProfilePhoto && !isMultipleDates
                  ? handleCircleIndicator()
                  : null}
              </g>
            </React.Fragment>
          );
        })}
      </svg>
    );
  }
);
